import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageHeader, Loader, Badge, Empty } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError, fileUrl } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { fmtDate, statusColor } from "@/lib/format";

export default function AdminKyc() {
  const { t } = useI18n();
  const [list, setList] = useState(null);
  const [sel, setSel] = useState(null);
  const [reason, setReason] = useState("");
  const load = () => api.get("/admin/kyc").then(({ data }) => setList(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!list) return <Loader />;

  const review = async (status) => {
    try {
      await api.put(`/admin/kyc/${sel.id}`, { status, reason });
      toast.success(`KYC ${status}`); setSel(null); setReason(""); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title={t("kyc")} subtitle="Review submitted documents." />
      {list.length === 0 ? <Empty label="No KYC submissions." /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((k) => (
            <div key={k.id} className="ec-card p-5" data-testid={`kyc-card-${k.id}`}>
              <div className="flex items-center justify-between"><p className="font-semibold text-white">{k.full_name}</p><Badge className={statusColor(k.status)}>{k.status.replace("_", " ")}</Badge></div>
              <p className="text-xs text-gray-500 mt-1 keep-ltr">ID: {k.id_number}</p>
              <p className="text-xs text-gray-500">{k.user_name} · {fmtDate(k.created_at)}</p>
              <Button size="sm" onClick={() => { setSel(k); setReason(k.reason || ""); }} data-testid={`review-kyc-${k.id}`} className="mt-4 w-full bg-orange-600 hover:bg-orange-500 font-semibold">Review</Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="ec-card border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{sel?.full_name} — KYC Review</DialogTitle></DialogHeader>
          {sel && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <p><span className="text-gray-500">ID:</span> <span className="text-white keep-ltr">{sel.id_number}</span></p>
                <p><span className="text-gray-500">Member:</span> <span className="text-white">{sel.user_name}</span></p>
              </div>
              {sel.address && <p className="text-sm text-gray-400">{sel.address}</p>}
              <div className="grid grid-cols-2 gap-3">
                {[["ID Front", sel.id_front], ["ID Back", sel.id_back], ["Photo", sel.profile_photo]].filter(([, p]) => p).map(([label, path]) => (
                  <div key={label}>
                    <p className="text-xs text-gray-500 mb-1">{label}</p>
                    <a href={fileUrl(path)} target="_blank" rel="noreferrer" data-testid={`kyc-doc-${label}`}>
                      <img src={fileUrl(path)} alt={label} className="rounded-lg border border-white/10 w-full h-32 object-cover hover:opacity-80" />
                    </a>
                  </div>
                ))}
              </div>
              <div><Input placeholder="Rejection reason / notes" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="kyc-reason-input" /></div>
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={() => review("approved")} data-testid="kyc-approve-button" className="bg-emerald-600 hover:bg-emerald-500 font-semibold">{t("approve")}</Button>
                <Button onClick={() => review("rejected")} data-testid="kyc-reject-button" className="bg-red-600 hover:bg-red-500 font-semibold">{t("reject")}</Button>
                <Button onClick={() => review("under_review")} className="bg-blue-600 hover:bg-blue-500 font-semibold">Under Review</Button>
                <Button onClick={() => review("additional_required")} className="bg-yellow-600 hover:bg-yellow-500 font-semibold">Request More</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
